export const maxFirstNameLength = 20;
export const maxLastNameLength = 20;
export const maxUsernameLength = 32;
export const maxGenderLength = 32;
export const maxTaglineLength = 60;

interface ImageLimitation {
  maxSize: number;
}

const imageLimitation: ImageLimitation = {
  maxSize: 20,
};

const BYTES_IN_MB = 1048576;

interface WebLink {
  name: string;
  address: string;
}

interface FileWithSize {
  size: number;
  name: string;
}

export const validateUsername = (value: string): string | undefined => {
  const patternID = /^id\d+$/;
  const patternUsername = /^[a-zA-Z0-9_-]*$/;

  const trimmedValue = value.trim();

  if (trimmedValue.match(patternID)) {
    return "Username shouldn't contain digits after 'id' symbols";
  }
  if (trimmedValue.length < 2) {
    return 'Minimum 2 symbols';
  }
  if (!trimmedValue.match(patternUsername)) {
    return "Username should contain only letters, numbers, and the symbols '-' and '_'";
  }
  if (trimmedValue.length > 32) {
    return 'Maximum 32 symbols';
  }
};

export const validateName = (value: string | undefined): string | undefined => {
  if (value?.trim()?.length && value.trim().length < 2) {
    return 'Minimum 2 symbols';
  }
};

export const validateFields = <T extends { [K in keyof T]: string }>(
  fields: T,
  isNewUsername?: boolean,
): Record<string, string | undefined> => {
  const errors: Record<string, string | undefined> = {};
  const fieldsRecord = fields as Record<string, string>;
  Object.entries(fieldsRecord).map(([key, value]) => {
    if (key === 'username' && isNewUsername) {
      errors[key] = validateUsername(value);
    }
    if (key === 'firstName' || key === 'lastName') {
      errors[key] = validateName(value);
    }
    if (key === 'email' && typeof value === 'string') {
      errors[key] = validateForEmptyField(value);
    }
    if (key === 'password') {
      errors[key] = validatePassword(value);
    }
    if (key === 'confirmPassword') {
      errors[key] = validateConfirmPassword(value, fieldsRecord?.password);
    }
    if (key === 'oldPassword') {
      errors[key] = validateForEmptyField(value);
    }
    return undefined;
  });
  return errors;
};

export const validateConfirmPassword = (
  confirmPassword: string,
  password: string,
): string | undefined => {
  if (!confirmPassword || confirmPassword !== password) {
    return 'Passwords do not match';
  }
};

export const validatePassword = (value: string): string | undefined => {
  const patternPassword = /^(?=.*?[A-Z])(?=.*?[a-z]).{4,32}$/;

  if (!value?.match(patternPassword)) {
    return 'Password should have at least one uppercase and one lowercase letter and contains more than 4 symbols';
  }
};

export const validateForEmptyField = (value: string): string | undefined => {
  if (!value.trim().length) {
    return 'Empty field';
  }
};

export const hasErrors = <T extends object>(errors: T): boolean => {
  return Object.values(errors).some((error) => error);
};

export const validateWebLinks = (
  webLink: WebLink,
  addedWebLinks: WebLink[],
): Record<string, string | undefined> => {
  const errors: Record<string, string | undefined> = {};
  const addedWebLinkNames: string[] = [];
  const addedWebLinkAddresses: string[] = [];

  addedWebLinks.map((link) => {
    addedWebLinkNames.push(link.name);
    addedWebLinkAddresses.push(link.address);
  });

  Object.entries(webLink).map(([key, value]) => {
    errors[key] = validateForEmptyField(value);

    const arr = key === 'name' ? addedWebLinkNames : addedWebLinkAddresses;

    if (arr.includes(value.trim())) {
      errors[key] = `Web link with this ${key} already exists`;
    }
  });

  return errors;
};

const isImageTypePass = (checkedValue: string): boolean => {
  const pattern = /(jpg|jpeg|png|heic)$/i;
  return pattern.test(checkedValue);
};

const getMegabytesFromBytes = (num: number): number => num / BYTES_IN_MB;

export const validateImage = (file: FileWithSize): string | undefined => {
  if (getMegabytesFromBytes(file.size) > imageLimitation.maxSize) {
    return `Maximum size is ${imageLimitation.maxSize} MB`;
  }

  if (!isImageTypePass(file.name)) {
    return 'Only jpg, jpeg, heic and png formats allowed';
  }
};

export const validateEmail = (value: string): boolean => {
  const EMAIL_REGEXP =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/iu;

  return EMAIL_REGEXP.test(value);
};

export const validateUrl = (str: string): string | undefined => {
  const domainPattern =
    /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,12}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/i;

  const createURL = (s: string): string | undefined => {
    if (validateForEmptyField(s)) {
      return validateForEmptyField(s);
    }

    if (!domainPattern.test(s)) {
      return 'Invalid URL';
    }

    const url = new URL(/^https?:\/\//i.test(s) ? s : 'http://' + s);

    if (
      !url.protocol ||
      (url.protocol !== 'http:' && url.protocol !== 'https:')
    ) {
      return 'Invalid URL';
    }
  };

  return createURL(str);
};
